import React, { useState, useEffect, useRef } from "react";
import { Select as AntdSelect, Input } from "antd";
import "./style.scss";
import { SelectArrow, RemoveSelectIcon, SearchIcon } from "assets/icons";
import Skeleton from "../skeleton";

const { Option } = AntdSelect;

function Select({
  label,
  options = [],
  className,
  labelClassName,
  inputClassName,
  popupClassName,
  widthVariant = "standard",
  placeholder = "Select",
  customWidthClass,
  mode,
  value,
  onChange,
  handleChange,
  isDisabled,
  isLoading,
  SkeletonHeight,
  returnObject,
  showSearch = true,
}) {
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const scrollYRef = useRef(0);
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const getWidthClass = () => {
    switch (widthVariant) {
      case "wFull":
        return "select-full";
      case "customWidth":
        return customWidthClass;
      case "standard":
      default:
        return "select-standard";
    }
  };

  const handleSelectChange = (val) => {
    let selectedOption;

    if (mode === "multiple") {
      selectedOption = options.filter((opt) => val.includes(opt.value));
    } else {
      selectedOption = options.find((opt) => opt.value === val);
    }

    let result;

    if (returnObject) {
      if (selectedOption) {
        if (Array.isArray(selectedOption)) {
          result = selectedOption.map((opt) => ({
            id: opt.id ?? opt.value,
            name: opt.name ?? opt.label ?? opt.value,
          }));
        } else {
          result = {
            id: selectedOption.id ?? selectedOption.value,
            name:
              selectedOption.name ??
              selectedOption.label ??
              selectedOption.value,
          };
        }
      } else {
        result = val;
      }
    } else {
      result = val;
    }

    setSearchValue("");

    onChange?.(result);
    handleChange?.(result);

    if (isMobile && mode !== "multiple") {
      setDropdownVisible(false);
    }
  };

  const handleSelectKeyDown = (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleSearchInputKeyDown = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!dropdownVisible) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (!isMobile && Math.abs(currentScrollY - scrollYRef.current) > 5) {
        setDropdownVisible(false);
        selectRef.current?.blur();
      }
      scrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dropdownVisible]);

  useEffect(() => {
    if (dropdownVisible && showSearch) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [dropdownVisible, showSearch]);

  const filteredOptions = options.filter((opt) =>
    opt?.label && typeof opt.label === "string"
      ? opt.label.toLowerCase().includes(searchValue.toLowerCase())
      : false,
  );

  return (
    <>
      {isLoading ? (
        <Skeleton label={label} height={SkeletonHeight} />
      ) : (
        <div className={`primary-select ${className}`}>
          {label && (
            <label className={`input-label ${labelClassName}`}>{label}</label>
          )}
          <AntdSelect
            ref={selectRef}
            value={value}
            mode={mode}
            showSearch={false}
            removeIcon={<RemoveSelectIcon />}
            popupMatchSelectWidth={false}
            className={`select-input ${getWidthClass()} ${inputClassName}`}
            popupClassName={`custom-dropdown-class ${popupClassName}`}
            onChange={handleSelectChange}
            placeholder={placeholder}
            allowClear
            suffixIcon={<SelectArrow className="custom-arrow" />}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} more`
            }
            disabled={isDisabled}
            open={dropdownVisible}
            onDropdownVisibleChange={(visible) => {
              if (isMobile) {
                if (
                  !visible &&
                  document.activeElement === searchInputRef.current?.input
                ) {
                  return;
                }
              }

              setDropdownVisible(visible);
            }}
            onInputKeyDown={handleSelectKeyDown}
            onKeyDown={handleSelectKeyDown}
            dropdownRender={(menu) => (
              <div className="dropdown-content-wrapper">
                {showSearch && (
                  <div
                    onScroll={(e) => e.stopPropagation()}
                    className="dropdown-search-wrapper"
                    style={{ padding: "8px" }}
                  >
                    <Input
                      ref={searchInputRef}
                      prefix={<SearchIcon />}
                      placeholder="Search..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      allowClear
                      className="select-search"
                      onKeyDown={handleSearchInputKeyDown}
                    />
                  </div>
                )}
                <div>{menu}</div>
              </div>
            )}
          >
            {filteredOptions.map((opt) => (
              <Option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </Option>
            ))}
          </AntdSelect>
        </div>
      )}
    </>
  );
}

export default Select;
